import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Banner } from '../../../models';
import { isExternalLink } from '../../../utils/';
import ImageLinkBanner from './components/ImageLinkBanner';
import BackgroundBanner from './components/BackgroundBanner';
import ImageOnlyBanner from './components/ImageOnlyBanner';
import InfoBanner from './components/InfoBanner';

type Props = {
    banner: Banner;
    onView?: (bannerId: string) => void | Promise<void>;
    onClick?: (bannerId: string, url?: string) => void | Promise<void>;
    onClose?: (bannerId: string) => void | Promise<void>;
    previewMode?: boolean;
}

const StaticBanner: React.FC<Props> = observer(({
    banner,
    onView,
    onClick,
    onClose,
    previewMode
}) => {
    useEffect(() => {
        if (!previewMode && onView) {
            onView(banner.id);
        }
    }, [banner.id, onView, previewMode]);

    const handleClose = () => {
        if (banner.staticHideable && onClose && !previewMode) {
            onClose(banner.id);
        }
    };

    const handleImageOrAreaClick = async () => {
        if (previewMode) return;

        if (banner.imageUrl && banner.bannerLink) {
            if (onClick) {
                await onClick(banner.id, banner.bannerLink);
            }
            const url = banner.bannerLink;
            if (isExternalLink(url)) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = url;
            }
        }
    };

    const handleLinkInTextClick = async (url: string) => {
        if (previewMode || !onClick) return;
        await onClick(banner.id, url);
    };

    const hasText = !!(banner.text && banner.text.trim());

    if (!banner.imageUrl) {
        return (
            <InfoBanner
                banner={banner}
                onClose={handleClose}
                onLinkClick={!previewMode && onClick ? handleLinkInTextClick : undefined}
                previewMode={previewMode}
            />
        );
    }

    if (hasText) {
        return (
            <BackgroundBanner
                banner={banner}
                onClose={handleClose}
                onClick={banner.bannerLink ? handleImageOrAreaClick : undefined}
                onLinkClick={!previewMode && onClick ? handleLinkInTextClick : undefined}
            />
        );
    }

    if (banner.bannerLink) {
        return (
            <ImageLinkBanner
                banner={banner}
                onClick={handleImageOrAreaClick}
                onClose={handleClose}
                previewMode={previewMode}
            />
        );
    }

    return (
        <ImageOnlyBanner
            banner={banner}
            onClose={handleClose}
        />
    );
});

export default StaticBanner;
